import { Injectable, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClient } from '@angular/common/http';
import { APIInterceptor } from './APIInterceptor.service';
import { StorageService } from './services/storage.service';
import { AuthGuardService } from './services/auth/auth-guard.service';
import { AuthAssetsService } from './services/auth/auth-assets.service';
import { BaseHttpService } from './services/base-http.service';
import { AuthLoginGuardService } from './services/auth/auth-login-guard.service';
import { AuthorizationService } from '@layouts/default/components/auth/auth.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { PhCollapseModule } from './components/ph-collapse/ph-collapse.module';
import { PhInputModule } from './components/ph-input/ph-input.module';
import { PhUploadModule } from './components/ph-upload/ph-upload.module';
import { PhAvatarModule } from './components/ph-avatar/ph-avatar.module';
import { ClickOutsideModule } from 'ng-click-outside';
import { RouterModule } from '@angular/router';
import { PhTabsetModule } from './components/ph-tabset/ph-tabset.module';
import { PhCropperModule } from '@shared/components/ph-cropper/ph-cropper.module';
import { FormattedDatePipe } from './pipes/formatted-date.pipe';
import { PhDropModule } from '@shared/components/ph-drop/ph-drop.module';
import { PhLabelModule } from '@shared/components/ph-label/ph-label.module';
import { PhSwiperModule } from '@shared/components/ph-swiper/ph-swiper.module';
import { PhChartModule } from '@shared/components/ph-chart/ph-chart.module';
import { PhSocialShareModule } from '@shared/components/ph-social-share/ph-social-share.module';
// Icons from font awesome currently not used so in the future someone can uninstall @fortawesone and remove from project
// import "@shared/social-icons";
import { ChartsModule } from 'ng2-charts';
import { HammertimeDirective } from './directives/hammertime.directive';

import * as Hammer from 'hammerjs';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig } from '@angular/platform-browser';
import { PhFiltersModule } from '@shared/components/ph-filters/ph-filters.module';
import { CurrencyAmountPipe } from './pipes/currency-amount.pipe';
import { CurrencyFormatterPipe } from './pipes/currency-formatter.pipe';
import { PhFiltersService } from './components/ph-filters/ph-filters.service';
import { ShortNumberPipe } from './pipes/short-number.pipe';
import { LangSwitcherComponent } from '@shared/components/header/header-desktop/components/lang-switcher/lang-switcher.component';
import { PhLoaderModule } from '@shared/components/ph-loader/ph-loader.module';
import { ToastrModule } from 'ngx-toastr';
import { AngularMyDatePickerModule } from 'angular-mydatepicker';
import { VotePipe } from './pipes/vote.pipe';
import { BaseFormComponent } from './components/forms/base-form.component';
import { CurrencyReversePipe } from './pipes/currency-reverse.pipe';
import { FirstVisitGuardService } from '@shared/services/auth/first-visit-guard.service';
import { NguCarouselModule } from '@ngu/carousel';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { PhProgressBarModule } from './components/ph-progress-bar/ph-progress-bar.module';
import { FacebookPixelService } from './services/facebook-pixel.service';
import { HttpLoaderFactory } from '../app.functions';
import { DialogModule } from 'primeng/dialog';
import { PhBannerModule } from './components/ph-banner/ph-banner.module';
import { HttpsPrefixPipe } from '@shared/pipes/https-prefix.pipe';
import { SvgShapedImageClipPathComponent } from '@layouts/default/components/svg-shaped-image-clip-path/svg-shaped-image-clip-path.component';
import { FormattedHeaderPipe } from '@shared/pipes/formatted-header.pipe';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { MobileAppBannerComponent } from '@layouts/default/components/mobile-app-banner/mobile-app-banner.component';
import { EnvPipe } from "@shared/pipes/env.pipe";
import { GoogleMapsService } from '@shared/services/google.maps.service';
import { FormattedAddressPipe } from '@shared/pipes/formatted-address.pipe';
import { DateRangePipe } from '@shared/pipes/date-range.pipe';
import { TagRouterLinkPipe } from '@shared/pipes/tag-router-link.pipe';
import { TagQueryParamsPipe } from '@shared/pipes/tag-query-params.pipe';
import { OpenStreetMapService } from '@shared/services/open-street-map.service';
import { OpenStreetMapWithMarkerModule } from '@layouts/default/components/open-street-map-with-marker/open-street-map-with-marker.module';

@Injectable()
export class HammerTimeConfig extends HammerGestureConfig {
  buildHammer(element: HTMLElement) {
    const hammer = new Hammer(element, {
      touchAction: 'pan-y',
    });
    return hammer;
  }
}

@NgModule({
    declarations: [
        EnvPipe,
        HttpsPrefixPipe,
        FormattedDatePipe,
        FormattedHeaderPipe,
        SafeHtmlPipe,
        HammertimeDirective,
        CurrencyAmountPipe,
        CurrencyFormatterPipe,
        ShortNumberPipe,
        LangSwitcherComponent,
        VotePipe,
        BaseFormComponent,
        CurrencyReversePipe,
        SvgShapedImageClipPathComponent,
        MobileAppBannerComponent,
        FormattedAddressPipe,
        DateRangePipe,
        TagRouterLinkPipe,
        TagQueryParamsPipe
    ],
    exports: [
        // from external pack
        TranslateModule,
        FormsModule,
        CommonModule,
        NgSelectModule,
        ClickOutsideModule,
        RouterModule,
        ReactiveFormsModule,
        ChartsModule,
        DialogModule,

        // custom
        PhCollapseModule,
        PhInputModule,
        PhUploadModule,
        PhAvatarModule,
        PhTabsetModule,
        PhCropperModule,
        EnvPipe,
        HttpsPrefixPipe,
        FormattedDatePipe,
        FormattedHeaderPipe,
        SafeHtmlPipe,
        PhDropModule,
        PhLabelModule,
        PhLoaderModule,
        PhSwiperModule,
        PhChartModule,
        PhSocialShareModule,
        HammertimeDirective,
        PhFiltersModule,
        CurrencyAmountPipe,
        CurrencyFormatterPipe,
        ShortNumberPipe,
        LangSwitcherComponent,
        VotePipe,
        CurrencyReversePipe,
        NguCarouselModule,
        PhProgressBarModule,
        SlickCarouselModule,
        PhBannerModule,
        SvgShapedImageClipPathComponent,
        MobileAppBannerComponent,
        FormattedAddressPipe,
        DateRangePipe,
        TagRouterLinkPipe,
        TagQueryParamsPipe,
        OpenStreetMapWithMarkerModule
    ],
  imports: [
    CommonModule,
    ToastrModule.forRoot(),
    HttpClientModule,
    NgSelectModule,
    AngularMyDatePickerModule,
    FormsModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      extend: true,
    }),
    DialogModule,
    ClickOutsideModule,
    RouterModule,
    ReactiveFormsModule,
    PhLoaderModule,
    PhTabsetModule,
    PhUploadModule,
    PhInputModule,
    PhCollapseModule,
    PhAvatarModule,
    PhCropperModule,
    PhDropModule,
    PhLabelModule,
    PhSwiperModule,
    PhChartModule,
    PhSocialShareModule,
    ChartsModule,
    PhFiltersModule,
    NguCarouselModule,
    PhProgressBarModule,
    SlickCarouselModule,
    OpenStreetMapWithMarkerModule
  ],
  providers: [
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerTimeConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: APIInterceptor,
      multi: true,
    },
    StorageService,
    AuthGuardService,
    AuthLoginGuardService,
    FirstVisitGuardService,
    AuthAssetsService,
    BaseHttpService,
    AuthorizationService,
    PhFiltersService,
    FacebookPixelService,
    GoogleMapsService,
    OpenStreetMapService
  ],
})
export class SharedModule {}
